import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { DEAL_PERMISSIONS } from "@/types/deals";
import { NewDealWizard } from "./NewDealWizard";
import { Modal } from "@/components/ui/Modal";

/**
 * Spec §8.4 — global pipeline shortcuts. Mounted once at the app shell
 * level (or near the top of any pipeline page); listens for:
 *   /     focus the first search input
 *   g d   navigate to /pipeline
 *   g h   navigate to /
 *   n d   open the New Deal wizard (Deals.Create only)
 *   ?     show the shortcut help overlay
 *   Esc   close the shortcut help overlay
 *
 * The chord (g + next key) listener watches a 1500ms window; pressing
 * any other key cancels the chord. The listener bails when the user is
 * focused inside an input / textarea / contenteditable so it doesn't
 * eat keystrokes during data entry.
 */
export function PipelineKeyboardShortcuts({ children }: { children?: ReactNode }) {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission(DEAL_PERMISSIONS.Create);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    let chord: "g" | "n" | null = null;
    let chordTimer: number | null = null;

    function clearChord() {
      chord = null;
      if (chordTimer) {
        window.clearTimeout(chordTimer);
        chordTimer = null;
      }
    }

    function isTypingTarget(t: EventTarget | null): boolean {
      if (!(t instanceof HTMLElement)) return false;
      if (t.isContentEditable) return true;
      const tag = t.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
    }

    function handler(e: KeyboardEvent) {
      // Always allow ? help overlay even outside inputs (Shift+/)
      if (e.key === "?" && !isTypingTarget(e.target)) {
        e.preventDefault();
        setHelpOpen(true);
        return;
      }
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === "/") {
        e.preventDefault();
        const search = document.querySelector<HTMLInputElement>(
          'input[type="search"], input[placeholder*="Search" i], input[aria-label*="Search" i]',
        );
        search?.focus();
        return;
      }

      if (chord === "g") {
        if (e.key === "d") {
          e.preventDefault();
          navigate("/pipeline");
        } else if (e.key === "h") {
          e.preventDefault();
          navigate("/");
        }
        clearChord();
        return;
      }
      if (chord === "n") {
        if (e.key === "d" && canCreate) {
          e.preventDefault();
          setWizardOpen(true);
        }
        clearChord();
        return;
      }

      if (e.key === "g" || e.key === "n") {
        chord = e.key;
        chordTimer = window.setTimeout(clearChord, 1500);
      }
    }

    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
      clearChord();
    };
  }, [canCreate, navigate]);

  return (
    <>
      {children}
      <NewDealWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
      <Modal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title="Keyboard shortcuts"
        size="sm"
      >
        <ul className="grid grid-cols-2 gap-space-2 text-small m-0 p-0 list-none">
          <ShortcutRow keys="/" label="Focus search" />
          <ShortcutRow keys="g d" label="Go to pipeline" />
          <ShortcutRow keys="g h" label="Go home" />
          <ShortcutRow keys="n d" label="New deal" />
          <ShortcutRow keys="?" label="This help" />
          <ShortcutRow keys="Esc" label="Close modal" />
        </ul>
      </Modal>
    </>
  );
}

function ShortcutRow({ keys, label }: { keys: string; label: string }) {
  return (
    <li className="flex items-center justify-between border border-border-light rounded-md px-space-2 py-space-1">
      <span className="text-onyx">{label}</span>
      <kbd className="font-mono text-caption px-space-2 py-[1px] rounded border border-border-light bg-white-warm">
        {keys}
      </kbd>
    </li>
  );
}
